import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { chatAPI, analyzeAPI, sessionAPI } from '../lib/api';
import { t } from '../lib/i18n';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const genMsgId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * All state + side-effects for the coach conversation: session loading,
 * text/photo sending, optimistic messages and error fallbacks.
 */
export const useCoachChat = (urlSessionId, language) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(urlSessionId || null);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const messagesContainerRef = useRef(null);

  const fr = language === 'fr';
  const errorReply = fr ? 'D\u00e9sol\u00e9, une erreur est survenue.' : 'Sorry, an error occurred.';

  const appendMessage = (msg) => setMessages(prev => [...prev, { id: genMsgId(), ...msg }]);

  const loadSessionMessages = useCallback(async (sid) => {
    setLoadingMessages(true);
    try {
      const res = await sessionAPI.messages(sid);
      setMessages(res.data.messages.map(m => ({
        role: m.role,
        content: m.content,
        hasImage: m.has_image || false,
        id: m._id || genMsgId(),
      })));
    } catch (err) {
      toast.error(fr ? 'Impossible de charger la session' : 'Could not load the session');
    } finally {
      setLoadingMessages(false);
    }
  }, [fr]);

  useEffect(() => {
    if (urlSessionId) {
      setSessionId(urlSessionId);
      loadSessionMessages(urlSessionId);
    }
  }, [urlSessionId, loadSessionMessages]);

  useEffect(() => {
    // Scroll only the messages pane, never the whole window (keeps header/composer in place).
    const el = messagesContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const adoptSession = (newSessionId) => {
    if (!sessionId && newSessionId) {
      setSessionId(newSessionId);
      navigate(`/coach/${newSessionId}`, { replace: true });
    }
  };

  const sendPhoto = async (file) => {
    appendMessage({ role: 'user', content: fr ? '[Photo envoy\u00e9e pour analyse]' : '[Photo uploaded for analysis]', hasImage: true });
    try {
      const res = await analyzeAPI.photo(file, language, sessionId);
      adoptSession(res.data.session_id);
      appendMessage({ role: 'assistant', content: res.data.analysis });
    } catch (err) {
      if (err.response?.data?.detail === 'photo_consent_required') {
        toast.error(t('consentRequired', language));
      } else {
        toast.error(fr ? 'Erreur d\'analyse' : 'Analysis error');
      }
      appendMessage({ role: 'assistant', content: errorReply });
    }
  };

  const sendText = async (text) => {
    appendMessage({ role: 'user', content: text });
    try {
      const res = await chatAPI.send(text, language, sessionId);
      adoptSession(res.data.session_id);
      appendMessage({ role: 'assistant', content: res.data.response });
    } catch (err) {
      toast.error(fr ? 'Erreur de communication' : 'Communication error');
      appendMessage({ role: 'assistant', content: errorReply });
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text && !selectedPhoto) return;
    setInput('');
    setLoading(true);
    if (selectedPhoto) {
      const file = selectedPhoto;
      clearPhoto();
      setSendingPhoto(true);
      await sendPhoto(file);
      setSendingPhoto(false);
    } else {
      await sendText(text);
    }
    setLoading(false);
  };

  const selectPhoto = (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error(fr ? 'Fichier trop volumineux (max 10MB)' : 'File too large (max 10MB)');
      return;
    }
    setSelectedPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    clearPhoto();
    navigate('/coach');
  };

  return {
    messages, input, setInput, loading, loadingMessages, sendingPhoto, selectedPhoto, photoPreview, messagesContainerRef,
    handleSend, selectPhoto, clearPhoto, startNewSession,
  };
};
